import * as React from 'react';
import {
  Box,
  CircularProgress,
  DialogContent,
  IconButton,
  InputAdornment,
  FormControl,
  InputLabel,
  MenuItem
} from '@mui/material';

import { useDispatch } from 'react-redux';
import CloseIcon from '@mui/icons-material/Close';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { CDialog, CDialogTitle, CDialogActions, CButton, CTextField, CSelect } from '../../components/common/Modal';

import { createUser, updateUser } from '../../slices/user';

export default function UserModal(props) {
  const {open, mode, data, handleClose} = props;

  const [loading, setLoading] = React.useState(false);
  const [role, setRole] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [firstname, setFirstname] = React.useState("");
  const [lastname, setLastname] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [confirmPassword, setConfirmPassword] = React.useState("");
  const [passwordError, setPasswordError] = React.useState("");
  const [showPassword, setShowPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);
  const [id, setId] = React.useState('');

  const dispatch = useDispatch();

  const roleMap = {
    'SUPER_ADMIN': 1,
    'ADMIN': 2,
    'USER': 3
  }

  React.useEffect(() => {
    if(mode === "Create") {
      setRole("");
      setEmail("");
      setFirstname("");
      setLastname("");
      setPassword("");
      setConfirmPassword("");
      setPasswordError("");
      setShowPassword(false);
      setShowConfirmPassword(false);
    } else {
      const {id: originId, email: originEmail, firstname: originFirstname, lastname: originLastname, role: originRole} = data;
      setId(originId);
      setEmail(originEmail);
      setFirstname(originFirstname);
      setLastname(originLastname);
      setRole(roleMap[originRole]);
    }
  }, [mode, data])

  const handleRole = (e) => {
    setRole(e.target.value)
  }

  const handleEmail = (e) => {
    setEmail(e.target.value)
  }

  const handleFirstname = (e) => {
    setFirstname(e.target.value)
  }
  
  const handleLastName = (e) => {
    setLastname(e.target.value)
  }

  const handlePassword = (e) => {
    setPassword(e.target.value);
    if (confirmPassword && e.target.value !== confirmPassword) {
      setPasswordError("Passwords do not match");
    } else {
      setPasswordError("");
    }
  }

  const handleConfirmPassword = (e) => {
    setConfirmPassword(e.target.value);
    if (password && e.target.value !== password) {
      setPasswordError("Passwords do not match");
    } else {
      setPasswordError("");
    }
  }

  const handleSave = (e) => {
    if (mode === 'Create') {
      if (!password) {
        setPasswordError("Password is required");
        return;
      }
      if (password !== confirmPassword) {
        setPasswordError("Passwords do not match");
        return;
      }
    }
    setLoading(true);
    let formData = new FormData();
    formData.append('email', email);
    formData.append('first_name', firstname);
    formData.append('last_name', lastname);
    formData.append('role', role);
    if(mode === 'Create') {
      formData.append('password', password);
      dispatch(createUser(formData))
        .then(res => {
          setLoading(false);
          handleClose();
        })
        .catch(err => {
          console.log(err);
          setLoading(false);
        }      
      )
    } else {
      formData.append('id', id);
      dispatch(updateUser(formData))
        .then(res => {
          setLoading(false);
          handleClose();
        })
        .catch(err => {
          console.log(err);
          setLoading(false);
        }      
      )
    }
  }
 
  return (
    <>
      <CDialog
        open={open}
        onClose={handleClose}
        maxWidth={"sm"}
        fullWidth
      >
        <CDialogTitle>New User</CDialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 6,
            top: 6,
            color: (theme) => theme.palette.grey[800],
          }}
        >
          <CloseIcon fontSize='small' />
        </IconButton>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: '1rem', px: '1.5rem', py: '1.25rem' }}>
          <FormControl size="small" fullWidth>
            <InputLabel id="role-label" sx={{ fontSize: '0.9rem', '&.Mui-focused': { color: '#6c63ff' } }}>Role</InputLabel>
            <CSelect
              labelId="role-label"
              id="role-select"
              value={role}
              label="Role"
              onChange={handleRole}
              size="small"
            >
              <MenuItem value={1}>SUPER ADMIN</MenuItem>
              <MenuItem value={2}>ADMIN</MenuItem>
              <MenuItem value={3}>USER</MenuItem>
            </CSelect>
          </FormControl>

          <CTextField
            id="email"
            label="Email"
            size="small"
            variant="outlined"
            value={email}
            onChange={handleEmail}
            fullWidth
          />

          <Box sx={{ display: 'flex', gap: '1rem' }}>
            <CTextField
              id="firstname"
              label="First name"
              size="small"
              variant="outlined"
              value={firstname}
              onChange={handleFirstname}
              sx={{ flex: 1 }}
            />
            <CTextField
              id="lastname"
              label="Last name"
              size="small"
              variant="outlined"
              value={lastname}
              onChange={handleLastName}
              sx={{ flex: 1 }}
            />
          </Box>

          {mode === 'Create' && (
            <>
              <CTextField
                id="password"
                label="Password"
                type={showPassword ? 'text' : 'password'}
                size="small"
                variant="outlined"
                value={password}
                onChange={handlePassword}
                error={!!passwordError}
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(v => !v)}
                        edge="end"
                        size="small"
                        tabIndex={-1}
                        sx={{ color: '#9e9e9e', '&:hover': { color: '#6c63ff' } }}
                      >
                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
              <CTextField
                id="confirmPassword"
                label="Confirm password"
                type={showConfirmPassword ? 'text' : 'password'}
                size="small"
                variant="outlined"
                value={confirmPassword}
                onChange={handleConfirmPassword}
                error={!!passwordError}
                helperText={passwordError}
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmPassword(v => !v)}
                        edge="end"
                        size="small"
                        tabIndex={-1}
                        sx={{ color: '#9e9e9e', '&:hover': { color: '#6c63ff' } }}
                      >
                        {showConfirmPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </>
          )}

          {loading && (
            <Box className="absolute w-full h-full" sx={{ top: 0, left: 0 }}>
              <Box className="relative w-full h-full">
                <Box className="w-full h-full flex justify-center align-center">
                  <CircularProgress sx={{ opacity: 0.7 }} />
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <CDialogActions>
          <CButton onClick={handleClose}>Cancel</CButton>
          <CButton onClick={handleSave}>Ok</CButton>
        </CDialogActions>
      </CDialog>
    </>
  )
}